"use client";

import { useState } from "react";
import Link from "next/link";
import { ArrowRight, Check, ChevronDown, Clock, Layers } from "lucide-react";
import { CourseIcon } from "./courseIcons";
import { lessonKey, useLearnProgress } from "./progress";

export type StatCourse = {
  slug: string;
  href: string;
  title: string;
  minutes: number;
  /* Lesson slugs, for the per-course progress this browser has stored. */
  lessons: string[];
  kind: "understand" | "build";
};

/* The same two steps, numbered the same way, as the sections further down the
   hub, so the popover reads as a map of the page. */
const groups: {
  kind: StatCourse["kind"];
  step: number;
  label: string;
  hint: string;
}[] = [
  {
    kind: "understand",
    step: 1,
    label: "Understand how it works",
    hint: "Read anywhere, no tool needed",
  },
  {
    kind: "build",
    step: 2,
    label: "Build it yourself",
    hint: "Hands-on, in your own account",
  },
];

function duration(minutes: number) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h ? `${h}h ${m}m` : `${m} min`;
}

/* One tile of the hub's stats strip that opens a list of every course on
   hover, keyboard focus or tap. The tile is a <dl> child, so the popover is a
   second <dd> rather than a stray element. */
export default function CoursesStat({
  courses,
  className,
  style,
  children,
}: {
  courses: StatCourse[];
  className: string;
  style: React.CSSProperties;
  children: React.ReactNode;
}) {
  const [hover, setHover] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [focus, setFocus] = useState(false);
  const open = hover || pinned || focus;
  const { done } = useLearnProgress();

  const totalLessons = courses.reduce((sum, c) => sum + c.lessons.length, 0);
  const totalMinutes = courses.reduce((sum, c) => sum + c.minutes, 0);
  /* "Start here" goes on the first course until this browser has begun any. */
  const anyStarted = courses.some((c) =>
    c.lessons.some((l) => done.has(lessonKey(c.slug, l))),
  );

  return (
    <div
      className={`relative cursor-pointer outline-none transition-colors duration-200 hover:bg-[var(--inset)] focus-visible:ring-2 focus-visible:ring-[#ffe500] focus-visible:ring-inset ${className}`}
      style={style}
      tabIndex={0}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => {
        setHover(false);
        setPinned(false);
      }}
      onClick={() => setPinned((p) => !p)}
      onFocus={(e) => {
        if (e.target.matches(":focus-visible")) setFocus(true);
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setFocus(false);
          setPinned(false);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          setHover(false);
          setPinned(false);
          setFocus(false);
        }
      }}
    >
      {children}
      {/* Affordance: a round disclosure button, centred on the tile's right
          edge once the tile lays out as a row. Decorative, since the popover
          itself is reachable by focus. */}
      <span
        className="pointer-events-none absolute right-4 top-4 lg:right-6 lg:top-1/2 lg:-translate-y-1/2"
        aria-hidden
      >
        {!open ? (
          <span
            className="absolute inset-0 rounded-full border motion-safe:animate-ping"
            style={{ borderColor: "var(--icon-gold-text)", opacity: 0.35 }}
          />
        ) : null}
        <span
          className={`relative flex h-8 w-8 items-center justify-center rounded-full border transition-[background-color,border-color,color,box-shadow] duration-200 ${
            open
              ? "bg-[#ffe500] text-black shadow-[0_4px_14px_-4px_rgba(255,229,0,0.6)]"
              : ""
          }`}
          style={{
            borderColor: open ? "#ffe500" : "var(--hairline-strong)",
            color: open ? undefined : "var(--icon-gold-text)",
            background: open ? undefined : "var(--icon-gold-bg)",
          }}
        >
          <ChevronDown
            className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
            strokeWidth={2.25}
          />
        </span>
      </span>
      <dd
        className={`absolute left-3 top-full z-30 w-[min(27rem,calc(100vw-2.5rem))] origin-top-left pt-3 transition-[opacity,transform] duration-200 ease-out sm:left-5 ${
          open
            ? "translate-y-0 scale-100 opacity-100"
            : "pointer-events-none -translate-y-1 scale-[0.98] opacity-0"
        }`}
        aria-hidden={!open}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="learn-popover relative cursor-default rounded-2xl border"
          style={{ borderColor: "var(--hairline-strong)" }}
        >
          {/* Caret pointing up at the tile. */}
          <span
            className="learn-popover absolute -top-[7px] left-8 h-3 w-3 rotate-45 border-l border-t"
            style={{ borderColor: "var(--hairline-strong)" }}
            aria-hidden
          />

          <div
            className="flex items-start justify-between gap-4 border-b px-5 pb-4 pt-5"
            style={{ borderColor: "var(--hairline)" }}
          >
            <div>
              <p className="font-heading text-[16px] font-bold leading-tight">
                {courses.length} free courses
              </p>
              <p className="mt-1.5 flex items-center gap-3 text-[12.5px] text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Layers className="h-3.5 w-3.5" aria-hidden />
                  {totalLessons} lessons
                </span>
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" aria-hidden />
                  {duration(totalMinutes)}
                </span>
              </p>
            </div>
            <span
              className="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold"
              style={{
                background: "var(--icon-gold-bg)",
                color: "var(--icon-gold-text)",
              }}
            >
              No signup to read
            </span>
          </div>

          <div className="max-h-[min(72vh,44rem)] overflow-y-auto overscroll-contain p-2">
            {groups.map(({ kind, step, label, hint }, gi) => (
              <div
                key={kind}
                className={gi > 0 ? "mt-1 border-t pt-1" : ""}
                style={{ borderColor: "var(--hairline)" }}
              >
                <div className="flex items-center gap-2.5 px-3 pb-1.5 pt-3">
                  <span
                    className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-black"
                    style={{ background: "#ffe500" }}
                    aria-hidden
                  >
                    {step}
                  </span>
                  <span className="text-[12.5px] font-semibold">{label}</span>
                  <span className="ml-auto text-[11.5px] text-muted-foreground">
                    {hint}
                  </span>
                </div>
                <ul>
                  {courses
                    .filter((c) => c.kind === kind)
                    .map((c, ci) => {
                      const n = c.lessons.filter((l) =>
                        done.has(lessonKey(c.slug, l)),
                      ).length;
                      const complete = n === c.lessons.length;
                      const startHere = !anyStarted && gi === 0 && ci === 0;
                      return (
                        <li key={c.href}>
                          <Link
                            href={c.href}
                            tabIndex={open ? 0 : -1}
                            className="group/row flex items-center gap-3 rounded-xl px-3 py-2 transition-colors hover:bg-[var(--inset)] focus-visible:bg-[var(--inset)] focus-visible:outline-none"
                          >
                            <span
                              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors duration-200 group-hover/row:bg-[#ffe500] group-hover/row:text-black group-focus-visible/row:bg-[#ffe500] group-focus-visible/row:text-black"
                              style={{
                                background: "var(--icon-gold-bg)",
                                color: "var(--icon-gold-text)",
                              }}
                              aria-hidden
                            >
                              <CourseIcon
                                slug={c.slug}
                                className="h-[15px] w-[15px]"
                              />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="flex items-center gap-2">
                                <span className="truncate text-[13.5px] font-semibold leading-snug">
                                  {c.title}
                                </span>
                                {startHere ? (
                                  <span className="shrink-0 rounded-full bg-[#ffe500] px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-black">
                                    Start here
                                  </span>
                                ) : null}
                              </span>
                              <span className="mt-0.5 block text-[12px] text-muted-foreground">
                                {c.lessons.length} lessons · {c.minutes} min
                                {n > 0 && !complete
                                  ? ` · ${n} of ${c.lessons.length} done`
                                  : ""}
                              </span>
                            </span>
                            {complete ? (
                              <span
                                className="inline-flex shrink-0 items-center gap-1 text-[11.5px] font-semibold"
                                style={{ color: "var(--icon-gold-text)" }}
                              >
                                <Check className="h-3.5 w-3.5" aria-hidden />
                                Done
                              </span>
                            ) : (
                              <ArrowRight
                                className="h-4 w-4 shrink-0 -translate-x-1 text-muted-foreground opacity-0 transition-[opacity,transform] duration-200 group-hover/row:translate-x-0 group-hover/row:opacity-100 group-focus-visible/row:translate-x-0 group-focus-visible/row:opacity-100"
                                aria-hidden
                              />
                            )}
                          </Link>
                        </li>
                      );
                    })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </dd>
    </div>
  );
}
